import React, { useState, useEffect } from 'react';
import { ToggleSettingCard } from '../../components/SettingCards/ToggleSetting/ToggleSetting';
import { ToggleSettingGroup } from '../../components/SettingCards/ToggleSetting/ToggleSettingGroup';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { SettingCardContent } from '../../components/SettingCards/SettingCard/SettingCardContent/SettingCardContent';
import './SessionReconnectSetting.css';
import { GENERAL_SETTING_IDS } from '../../config/SettingIds';
import {
	DropdownSettingConfig,
	getSettingConfig,
	ToggleSettingConfig,
	ToggleSettingGroupConfig,
} from '../../config/SettingsDefinitions';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';

export const SessionReconnectSetting: React.FC = () => {
	// State for toggle settings
	const [sessionReconnectEnabled, setSessionReconnectEnabled] = useState(true);
	const [sessionReconnectSettingEnabled, setSessionReconnectSettingEnabled] =
		useState(true); // Tracks if the setting itself is enabled from bridge
	const [reconnectOnAppDesktop, setReconnectOnAppDesktop] = useState(true);
	const [reconnectOnWorkspaceRefresh, setReconnectOnWorkspaceRefresh] = useState(true);
	const [reconnectOnWorkspaceLogin, setReconnectOnWorkspaceLogin] = useState(true);
	const [reconnectOnWindowsLogin, setReconnectOnWindowsLogin] = useState(true);

	// State for dropdown settings
	const [sessionReconnect, setSessionReconnect] = useState('active-and-disconnected');

	// Load session reconnect settings
	useEffect(() => {
		const loadSessionReconnectSettings = async () => {
			try {
				const nativeBridge = SettingsNativeBridgeFactory.getBridge();
				const settingsResponse = await nativeBridge.getSettings({
					settings: [
						{ settingKey: GENERAL_SETTING_IDS.SESSION_RECONNECT },
						{ settingKey: GENERAL_SETTING_IDS.SESSION_RECONNECT_TO },
						{ settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_APP_DESKTOP },
						{ settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_REFRESH },
						{ settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_LOGIN },
						{ settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_WINDOWS_LOGIN },
					],
				});

				if (settingsResponse && settingsResponse.status === 0) {
					const settings = settingsResponse.data.settings || [];
					const settingsLookup: Record<string, any> = {};
					settings.forEach((setting) => {
						settingsLookup[setting.settingKey] = setting;
					});

					// Update main session reconnect setting
					const sessionReconnectData =
						settingsLookup[GENERAL_SETTING_IDS.SESSION_RECONNECT];
					if (sessionReconnectData) {
						if (sessionReconnectData.settingValue !== undefined) {
							setSessionReconnectEnabled(Boolean(sessionReconnectData.settingValue));
						}
						if (sessionReconnectData.enabled !== undefined) {
							setSessionReconnectSettingEnabled(sessionReconnectData.enabled);
						}
					}

					// Update dropdown setting
					const sessionReconnectToData =
						settingsLookup[GENERAL_SETTING_IDS.SESSION_RECONNECT_TO];
					if (sessionReconnectToData?.settingValue !== undefined) {
						setSessionReconnect(sessionReconnectToData.settingValue);
					}

					// Update subsettings
					const appDesktopData =
						settingsLookup[GENERAL_SETTING_IDS.RECONNECT_ON_APP_DESKTOP];
					if (appDesktopData?.settingValue !== undefined) {
						setReconnectOnAppDesktop(Boolean(appDesktopData.settingValue));
					}

					const workspaceRefreshData =
						settingsLookup[GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_REFRESH];
					if (workspaceRefreshData?.settingValue !== undefined) {
						setReconnectOnWorkspaceRefresh(Boolean(workspaceRefreshData.settingValue));
					}

					const workspaceLoginData =
						settingsLookup[GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_LOGIN];
					if (workspaceLoginData?.settingValue !== undefined) {
						setReconnectOnWorkspaceLogin(Boolean(workspaceLoginData.settingValue));
					}

					const windowsLoginData =
						settingsLookup[GENERAL_SETTING_IDS.RECONNECT_ON_WINDOWS_LOGIN];
					if (windowsLoginData?.settingValue !== undefined) {
						setReconnectOnWindowsLogin(Boolean(windowsLoginData.settingValue));
					}
				}
			} catch (error) {
				const errorMessage = `Failed to load session reconnect settings: ${error}`;
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					errorMessage,
					AotCategory.Error,
					errorMessage
				);
			}
		};

		loadSessionReconnectSettings();
	}, []);

	const handleSessionReconnectToggleChange = async (value: boolean) => {
		setSessionReconnectEnabled(value);

		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.SESSION_RECONNECT,
				settingValue: value,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved session reconnect setting: ${value}`,
				AotCategory.Info,
				`Successfully saved session reconnect setting: ${value}`
			);
		} catch (error) {
			const errorMessage = `Failed to save session reconnect setting: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
		}
	};

	const handleSessionReconnectChange = async (value: string) => {
		setSessionReconnect(value);

		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.SESSION_RECONNECT_TO,
				settingValue: value,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved session reconnect dropdown setting: ${value}`,
				AotCategory.Info,
				`Successfully saved session reconnect dropdown setting: ${value}`
			);
		} catch (error) {
			const errorMessage = `Failed to save session reconnect dropdown setting: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
		}
	};

	const handleReconnectOnAppDesktopChange = async (value: boolean) => {
		setReconnectOnAppDesktop(value);
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_APP_DESKTOP,
				settingValue: value,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved reconnect on app desktop: ${value}`,
				AotCategory.Info,
				`Successfully saved reconnect on app desktop: ${value}`
			);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save reconnect on app desktop: ${error}`,
				AotCategory.Error,
				`Failed to save reconnect on app desktop: ${error}`
			);
		}
	};

	const handleReconnectOnWorkspaceRefreshChange = async (value: boolean) => {
		setReconnectOnWorkspaceRefresh(value);
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_REFRESH,
				settingValue: value,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved reconnect on workspace refresh: ${value}`,
				AotCategory.Info,
				`Successfully saved reconnect on workspace refresh: ${value}`
			);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save reconnect on workspace refresh: ${error}`,
				AotCategory.Error,
				`Failed to save reconnect on workspace refresh: ${error}`
			);
		}
	};

	const handleReconnectOnWorkspaceLoginChange = async (value: boolean) => {
		setReconnectOnWorkspaceLogin(value);
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_LOGIN,
				settingValue: value,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved reconnect on workspace login: ${value}`,
				AotCategory.Info,
				`Successfully saved reconnect on workspace login: ${value}`
			);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save reconnect on workspace login: ${error}`,
				AotCategory.Error,
				`Failed to save reconnect on workspace login: ${error}`
			);
		}
	};

	const handleReconnectOnWindowsLoginChange = async (value: boolean) => {
		setReconnectOnWindowsLogin(value);
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.RECONNECT_ON_WINDOWS_LOGIN,
				settingValue: value,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved reconnect on windows login: ${value}`,
				AotCategory.Info,
				`Successfully saved reconnect on windows login: ${value}`
			);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save reconnect on windows login: ${error}`,
				AotCategory.Error,
				`Failed to save reconnect on windows login: ${error}`
			);
		}
	};

	const sessionReconnectSettingConfig = getSettingConfig(
		GENERAL_SETTING_IDS.SESSION_RECONNECT
	) as ToggleSettingConfig;

	return (
		<ToggleSettingCard
			settingId={GENERAL_SETTING_IDS.SESSION_RECONNECT}
			title={sessionReconnectSettingConfig.title}
			description={sessionReconnectSettingConfig.description}
			checked={sessionReconnectEnabled}
			onChange={handleSessionReconnectToggleChange}
			disabled={!sessionReconnectSettingEnabled}
		>
			<SettingCardContent>
				<>
					<Dropdown
						options={
							(sessionReconnectSettingConfig.subSettings?.[0] as DropdownSettingConfig)
								?.options || []
						}
						value={sessionReconnect}
						onChange={handleSessionReconnectChange}
						label={
							(sessionReconnectSettingConfig.subSettings?.[0] as DropdownSettingConfig)
								?.label
						}
						id={GENERAL_SETTING_IDS.SESSION_RECONNECT_TO}
						disabled={!sessionReconnectSettingEnabled || !sessionReconnectEnabled}
						className="dropdown-margin"
					/>
					<ToggleSettingGroup
						settings={[
							{
								settingId: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[0]?.id,
								title: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[0]?.title,
								checked: reconnectOnAppDesktop,
								onChange: handleReconnectOnAppDesktopChange,
								disabled: !sessionReconnectSettingEnabled || !sessionReconnectEnabled,
							},
							{
								settingId: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[1]?.id,
								title: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[1]?.title,
								checked: reconnectOnWorkspaceRefresh,
								onChange: handleReconnectOnWorkspaceRefreshChange,
								disabled: !sessionReconnectSettingEnabled || !sessionReconnectEnabled,
							},
							{
								settingId: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[2]?.id,
								title: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[2]?.title,
								checked: reconnectOnWorkspaceLogin,
								onChange: handleReconnectOnWorkspaceLoginChange,
								disabled: !sessionReconnectSettingEnabled || !sessionReconnectEnabled,
							},
							{
								settingId: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[3]?.id,
								title: (
									sessionReconnectSettingConfig
										.subSettings?.[1] as ToggleSettingGroupConfig
								)?.toggleOptions[3]?.title,
								checked: reconnectOnWindowsLogin,
								onChange: handleReconnectOnWindowsLoginChange,
								disabled: !sessionReconnectSettingEnabled || !sessionReconnectEnabled,
							},
						]}
					/>
				</>
			</SettingCardContent>
		</ToggleSettingCard>
	);
};
